/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as GuildNSFWActionCreators from '@app/actions/GuildNSFWActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import styles from '@app/components/channel/NSFWChannelGate.module.css';
import {ExternalLink} from '@app/components/common/ExternalLink';
import {AgeVerificationModal} from '@app/components/modals/AgeVerificationModal';
import {Button} from '@app/components/uikit/button/Button';
import {HelpCenterArticleSlug} from '@app/constants/HelpCenterConstants';
import {NSFWGateReason} from '@app/stores/GuildNSFWAgreeStore';
import {getRegionDisplayName} from '@app/utils/GeoUtils';
import * as HelpCenterUtils from '@app/utils/HelpCenterUtils';
import {getEffectiveNSFWGeoContext, isAgeVerificationAvailableInRegion} from '@app/utils/NSFWGeoUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';

interface Props {
	channelId: string;
	guildId?: string | null;
	reason: NSFWGateReason;
	scope: 'channel' | 'guild';
}

export const NSFWChannelGate = observer(({channelId, guildId, reason, scope}: Props) => {
	const {i18n} = useLingui();
	const handleOpenAgeVerification = useCallback(() => {
		ModalActionCreators.push(modal(() => <AgeVerificationModal />));
	}, []);
	const handleProceed = () => {
		if (scope === 'guild' && guildId) {
			GuildNSFWActionCreators.agreeToGuild(guildId);
			return;
		}

		GuildNSFWActionCreators.agreeToChannel(channelId);
	};

	const renderContent = () => {
		switch (reason) {
			case NSFWGateReason.GEO_RESTRICTED: {
				const {countryCode, regionCode} = getEffectiveNSFWGeoContext();
				const regionName = getRegionDisplayName(i18n, countryCode ?? undefined, regionCode ?? undefined);
				const ageVerificationAvailable = isAgeVerificationAvailableInRegion();

				return (
					<>
						<h2 className={styles.title}>
							<Trans>Age-Restricted Content</Trans>
						</h2>
						{ageVerificationAvailable ? (
							<p className={styles.description}>
								<Trans>
									Due to age verification laws in {regionName}, you cannot access age-restricted content from this
									region. You can verify your age using a credit card to unlock this content.
								</Trans>
							</p>
						) : (
							<p className={styles.description}>
								<Trans>
									Due to age verification laws in {regionName}, you cannot access age-restricted content from this
									region. Age verification is currently available only in the UK.
								</Trans>
							</p>
						)}
						{ageVerificationAvailable && (
							<Button onClick={handleOpenAgeVerification} variant="primary">
								<Trans>Verify Your Age</Trans>
							</Button>
						)}
					</>
				);
			}

			case NSFWGateReason.AGE_RESTRICTED:
				return scope === 'guild' ? (
					<>
						<h2 className={styles.title}>
							<Trans>Age-Restricted Community</Trans>
						</h2>
						<p className={styles.description}>
							<Trans>
								You must be 18 or older to view this community.{' '}
								<ExternalLink href={HelpCenterUtils.getURL(HelpCenterArticleSlug.ChangeDateOfBirth)}>
									Learn more
								</ExternalLink>
							</Trans>
						</p>
					</>
				) : (
					<>
						<h2 className={styles.title}>
							<Trans>Age-Restricted Channel</Trans>
						</h2>
						<p className={styles.description}>
							<Trans>
								You must be 18 or older to view this channel.{' '}
								<ExternalLink href={HelpCenterUtils.getURL(HelpCenterArticleSlug.ChangeDateOfBirth)}>
									Learn more
								</ExternalLink>
							</Trans>
						</p>
					</>
				);
			default:
				return scope === 'guild' ? (
					<>
						<h2 className={styles.title}>
							<Trans>Age-Restricted Community</Trans>
						</h2>
						<p className={styles.description}>
							<Trans>
								This community is marked as age-restricted and may contain content that is not safe for work or that may
								be inappropriate for some users.
							</Trans>
						</p>
						<Button onClick={handleProceed} variant="danger-primary">
							<Trans>Proceed</Trans>
						</Button>
					</>
				) : (
					<>
						<h2 className={styles.title}>
							<Trans>NSFW Channel</Trans>
						</h2>
						<p className={styles.description}>
							<Trans>
								This channel is marked as NSFW and may contain content that is not safe for work or that may be
								inappropriate for some users.
							</Trans>
						</p>
						<Button onClick={handleProceed} variant="danger-primary">
							<Trans>Proceed</Trans>
						</Button>
					</>
				);
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.iconContainer}>
					<WarningIcon className={styles.icon} weight="fill" />
				</div>
				{renderContent()}
			</div>
		</div>
	);
});
